import React, { useMemo, useContext } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCheckbox,
  MDBTypography,
  MDBBtn,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBBadge
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { DevContext } from '../../../../App';
import { getConditionsForLectureType, getRequiredConditionsForLectureType } from '../validation/lectures.js';
import { getLocalizedText } from '../../../../utils/localizedText';

export default function ConditionsStep({ onComplete, stepId }) {
  const { t } = useTranslation();
  const { formData, handleFieldChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep } = useStepNavigation();
  const { getConditionsValidation, getStepValidation, getStepRequiredFields, eventConfig } = useValidation();
  const { isDevMode } = useContext(DevContext);

  // Get dynamic conditions based on lecture type
  const availableConditions = useMemo(() => {
    if (!formData.type || !eventConfig?.eventConditions) {
      return [];
    }
    
    return getConditionsForLectureType(eventConfig, formData.type);
  }, [formData.type, eventConfig]);

  // Get required conditions for current lecture type
  const requiredConditions = useMemo(() => {
    if (!formData.type || !eventConfig?.eventConditions) {
      return [];
    }
    
    return getRequiredConditionsForLectureType(eventConfig, formData.type);
  }, [formData.type, eventConfig]);

  // Note: getLocalizedText already handles i18n language correctly

  const handleConditionChange = (conditionId, checked) => {
    const currentConditions = formData.conditions || [];
    const condition = availableConditions.find(c => c.id === conditionId);
    
    if (checked) {
      // Add condition if not exists
      const conditionExists = currentConditions.some(c => c.id === conditionId);
      if (!conditionExists && condition) {
        const newConditions = [...currentConditions, { 
          id: conditionId, 
          accepted: true 
        }];
        handleFieldChange('conditions', newConditions);
      }
    } else {
      // Remove condition or mark as not accepted
      const newConditions = currentConditions.filter(c => c.id !== conditionId);
      handleFieldChange('conditions', newConditions);
    }
  };

  const isConditionChecked = (conditionId) => {
    return formData.conditions?.some(c => c.id === conditionId && c.accepted) || false;
  };

  const areAllRequiredConditionsChecked = () => {
    return requiredConditions.every(condition => isConditionChecked(condition.id));
  };

  const areAllAvailableConditionsHandled = () => {
    // Check if all applicable conditions have been addressed (either accepted or explicitly rejected)
    return availableConditions.every(condition => {
      const userCondition = formData.conditions?.find(c => c.id === condition.id);
      return userCondition !== undefined; // User has made a choice
    });
  };

  // Get validation info from schema
  const conditionsValidation = getConditionsValidation();
  const stepValidation = getStepValidation('conditions');
  const requiredFields = getStepRequiredFields('conditions');

  const handleContinue = () => {
    if (areAllRequiredConditionsChecked() && stepValidation.isValid) {
      onComplete?.(stepId);
      goToNextStep();
    }
  };

  // Helper function to get condition text in current language
  const getConditionText = (condition) => {
    return getLocalizedText(condition.content) || condition.id;
  };

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1">
        <MDBTypography tag="h3" className="mb-4">
          {t('Please agree to our conditions')}
        </MDBTypography>
      
      {!formData.type && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBAlert color="warning">
              {t('Please select a lecture type first to see applicable conditions.')}
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}
      
      {formData.type && availableConditions.length === 0 && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBAlert color="info">
              {t('No specific conditions required for this lecture type.')}
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}
      
      {availableConditions.length > 0 && (
        <MDBRow className="mb-4">
          <MDBCol>
            {availableConditions.map((condition) => (
              <div key={condition.id} className="mb-3">
                <MDBCheckbox
                  name={condition.id}
                  id={condition.id}
                  label={
                    <span>
                      {getConditionText(condition)}
                      {condition.required && (
                        <MDBBadge color="danger" className="ms-2">
                          {t('Required')}
                        </MDBBadge>
                      )}
                    </span>
                  }
                  checked={isConditionChecked(condition.id)}
                  onChange={(e) => handleConditionChange(condition.id, e.target.checked)}
                />
              </div>
            ))}
          </MDBCol>
        </MDBRow>
      )}

      {hasFieldError('conditions') && (
        <MDBRow className="mb-3">
          <MDBCol>
            <MDBAlert color="danger">
              {getFieldError('conditions')}
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {/* Validation Debug Info */}
      {isDevMode && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBCard className="border-info">
              <MDBCardBody>
                <MDBTypography tag="h6" className="mb-3">
                  🔍 Validation Debug Info (Dev Mode) - Conditions Step
                </MDBTypography>
              
              <div className="mb-2">
                <strong>Lecture Type:</strong> {formData.type || 'Not selected'}
              </div>
              
              <div className="mb-2">
                <strong>Dynamic Conditions:</strong>
                <ul className="mb-2">
                  <li>Available: {availableConditions.length} conditions for type "{formData.type}"</li>
                  <li>Required: {requiredConditions.length} conditions must be accepted</li>
                  <li>Current Accepted: {formData.conditions?.filter(c => c.accepted)?.length || 0}</li>
                  <li>Step Required Fields: {requiredFields.join(', ') || 'none'}</li>
                </ul>
              </div>
              
              {availableConditions.length > 0 && (
                <div className="mb-2">
                  <strong>Condition Details:</strong>
                  <ul className="mb-2">
                    {availableConditions.map((condition) => (
                      <li key={condition.id}>
                        <code>{condition.id}</code>: {condition.required ? 'Required' : 'Optional'} - 
                        {isConditionChecked(condition.id) ? ' ✓ Accepted' : ' ✗ Not accepted'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="mb-2">
                <strong>Validation Status:</strong>{' '}
                <MDBBadge color={stepValidation.isValid ? 'success' : 'danger'}>
                  {stepValidation.isValid ? 'Valid' : 'Invalid'}
                </MDBBadge>
              </div>
              
              {stepValidation.errors.length > 0 && (
                <div className="mb-2">
                  <strong>Schema Errors:</strong>
                  <ul className="mb-0">
                    {stepValidation.errors.map((error, index) => (
                      <li key={index}>
                        <code>{error.field}</code>: {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="mb-2">
                <strong>Continue Requirements:</strong>
                <ul className="mb-2">
                  <li>All required conditions checked: {areAllRequiredConditionsChecked() ? '✓' : '✗'}</li>
                  <li>Schema validation passed: {stepValidation.isValid ? '✓' : '✗'}</li>
                </ul>
              </div>
              
              <div className="mb-2">
                <strong>Continue Button:</strong>{' '}
                <MDBBadge color={areAllRequiredConditionsChecked() && stepValidation.isValid ? 'success' : 'warning'}>
                  {areAllRequiredConditionsChecked() && stepValidation.isValid ? 'Enabled' : 'Disabled'}
                </MDBBadge>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      )}
      </MDBContainer>
      
      <MDBContainer className="mt-auto">
        <MDBRow>
          <MDBCol className="d-flex justify-content-center">
            <MDBBtn 
              onClick={handleContinue}
              disabled={!areAllRequiredConditionsChecked() || !stepValidation.isValid || !formData.type}
            >
              {t('Continue')}
            </MDBBtn>
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    </div>
  );
}